/**
 * Loyalty card colours (Jason, 26 Oct 2026): the progress-bar colour tiers the app shows on the
 * DeeReward card (system_setting.loyalty_tier_config, read by the app through loyalty-cards;
 * apps/mobile loyaltyTierHelper.ts). `minCycles` counts 10-point cycles of points earned. This is
 * NOT the spend bands above: it only colours the card. It used to sit on the Voucherify Rewards
 * Program page (removed); the setting itself was never Voucherify.
 */
import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Skeleton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useNotify } from 'react-admin';
import authInstance from '../../../services/httpClient';

interface CardTier { name: string; minCycles: number; color: string }

const API = '/admin/promotions/loyalty-tier-config';
const HEX = /^#[0-9A-Fa-f]{6}$/;

export const CardColoursEditor: React.FC<{ readOnly: boolean }> = ({ readOnly }) => {
  const notify = useNotify();
  const [tiers, setTiers] = useState<CardTier[] | null>(null);
  const [version, setVersion] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    authInstance.get(API)
      .then(({ data }) => { setTiers(data?.data?.tiers ?? []); setVersion(data?.data?.version ?? null); })
      .catch((err) => { setTiers([]); setLoadFailed(true); notify(err?.response?.data?.error || 'Could not load the card colours', { type: 'error' }); });
  }, [notify]);

  if (!tiers) return <Skeleton variant="rounded" height={160} data-testid="card-colours-loading" />;
  if (loadFailed) return <Typography variant="body2" color="error">Could not load the card colours. Reload the page to edit them.</Typography>;

  const set = (i: number, patch: Partial<CardTier>) => setTiers((ts) => ts!.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  const invalid = tiers.length === 0 || tiers.some((t) => !t.name.trim() || !(t.minCycles >= 0) || !HEX.test(t.color))
    || !tiers.some((t) => t.minCycles === 0)
    || new Set(tiers.map((t) => Number(t.minCycles))).size !== tiers.length;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await authInstance.put(API, { tiers: tiers.map((t) => ({ ...t, name: t.name.trim(), minCycles: Number(t.minCycles) })) });
      setTiers(data?.data?.tiers ?? tiers);
      setVersion(data?.data?.version ?? version);
      notify('Card colours saved: the app picks them up on its next refresh', { type: 'success' });
    } catch (err: any) {
      notify(err?.response?.data?.message || err?.response?.data?.error || 'Could not save the card colours', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={1.5} data-testid="card-colours-form">
      {tiers.map((t, i) => (
        <Stack key={i} direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: HEX.test(t.color) ? t.color : 'transparent', border: '1px solid', borderColor: 'divider', flexShrink: 0 }} />
          <TextField size="small" label="Name" value={t.name} disabled={readOnly} onChange={(e) => set(i, { name: e.target.value })} sx={{ width: 160 }}
            inputProps={{ 'aria-label': `Card colour ${i + 1} name` }} />
          <TextField size="small" label="From cycle" type="number" value={t.minCycles} disabled={readOnly} onChange={(e) => set(i, { minCycles: Number(e.target.value) })} sx={{ width: 120 }}
            inputProps={{ min: 0, 'aria-label': `Card colour ${i + 1} from cycle` }} />
          <TextField size="small" label="Colour" value={t.color} disabled={readOnly} onChange={(e) => set(i, { color: e.target.value })} sx={{ width: 130 }}
            error={!HEX.test(t.color)} inputProps={{ 'aria-label': `Card colour ${i + 1} hex` }} />
          {!readOnly && (
            <Tooltip title="Remove">
              <span>
                <IconButton size="small" onClick={() => setTiers((ts) => ts!.filter((_, j) => j !== i))} disabled={tiers.length <= 1} aria-label={`Remove card colour ${i + 1}`}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      ))}
      <Typography variant="caption" color="text.secondary">
        A cycle is 10 points earned. The first colour must start at cycle 0, and no two colours can start at the same cycle. Colours are hex, e.g. #42C684.{version != null ? ` Saved version ${version}.` : ''}
      </Typography>
      {!readOnly && (
        <Stack direction="row" spacing={1}>
          <Button size="small" startIcon={<AddIcon />} onClick={() => setTiers((ts) => [...ts!, { name: '', minCycles: (ts!.at(-1)?.minCycles ?? 0) + 10, color: '#42C684' }])}>
            Add colour
          </Button>
          <Button size="small" variant="contained" onClick={save} disabled={saving || invalid}>
            {saving ? 'Saving...' : 'Save card colours'}
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
